import React, { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { Banner } from '../../common';
import type { BannerVariant } from '../../common';
import { useCanPerform } from '../../../hooks/useCanPerform';
import { DischargeSummaryService } from '../../../services/patientmr/dischargeSummary.service';
import { describeApiError } from '../../../utils/apiErrors';
import type { DischargeSummaryDueState, DischargeSummaryListItem } from '../../../dtos/patientmr/dischargeSummary.dto';
import DischargeSummaryDialog from './DischargeSummaryDialog';
import { DUE_LABEL, KIND_LABEL, STATUS_LABEL, formatUsDate } from './dischargeSummaryLabels';

export interface DischargeSummaryBannerProps {
  patientId: string;
  /** Patient status as the header shows it (INACTIVE, ON_HOLD, …) */
  status: string | null | undefined;
  /** Bump to reload, e.g. after a discharge or hold */
  refreshKey?: number;
}

const VARIANT: Record<DischargeSummaryDueState, BannerVariant> = {
  DUE: 'info',
  DUE_TODAY: 'warning',
  OVERDUE: 'error',
  SENT: 'neutral',
  NOT_REQUIRED: 'neutral',
};

const actionClass = 'inline-flex min-h-[40px] items-center gap-1 rounded-md border border-slate-400 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50';

/** Open work first; a sent discharge summary stays visible on a discharged patient. */
export function summariesToShow(summaries: DischargeSummaryListItem[], status: string | null | undefined): DischargeSummaryListItem[] {
  const open = summaries.filter((s) => s.dueState === 'DUE' || s.dueState === 'DUE_TODAY' || s.dueState === 'OVERDUE');
  if (String(status).toUpperCase() !== 'INACTIVE') return open;
  const latestSent = summaries.find((s) => s.kind === 'DISCHARGE' && s.dueState === 'SENT');
  return latestSent && !open.some((s) => s.summaryId === latestSent.summaryId) ? [...open, latestSent] : open;
}

/**
 * Discharge / transfer summary status under the patient header (Phase 16E, DSC-14): status, due
 * date and an Open button; on a discharged patient with no summary (e.g. Deceased), Create.
 */
const DischargeSummaryBanner: React.FC<DischargeSummaryBannerProps> = ({ patientId, status, refreshKey = 0 }) => {
  const canRead = useCanPerform('read:dischargesummary');
  const canUpdate = useCanPerform('update:dischargesummary');
  const [summaries, setSummaries] = useState<DischargeSummaryListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const relevant = ['INACTIVE', 'ON_HOLD'].includes(String(status).toUpperCase());

  const load = useCallback(async () => {
    if (!patientId || !canRead || !relevant) return;
    try {
      setError(null);
      setSummaries(await DischargeSummaryService.list(patientId));
    } catch (err) {
      setError(describeApiError(err, 'Could not load discharge summaries.'));
    }
  }, [patientId, canRead, relevant]);

  useEffect(() => {
    setSummaries(null);
    void load();
  }, [load, refreshKey]);

  if (!canRead || !relevant) return null;
  if (error) {
    return (
      <Banner variant="warning" title="Discharge summaries unavailable" action={<button type="button" className={actionClass} onClick={() => void load()}>Retry</button>}>
        {error}
      </Banner>
    );
  }
  if (!summaries) return null;

  const shown = summariesToShow(summaries, status);
  const missingDischarge = String(status).toUpperCase() === 'INACTIVE' && !summaries.some((s) => s.kind === 'DISCHARGE');

  const create = async () => {
    setCreating(true);
    try {
      const created = await DischargeSummaryService.create(patientId, 'DISCHARGE');
      await load();
      setOpenId(created.summaryId);
    } catch (err) {
      setError(describeApiError(err, 'Could not create the summary.'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {shown.map((s) => (
        <Banner
          key={s.summaryId}
          variant={VARIANT[s.dueState]}
          title={`${KIND_LABEL[s.kind]} — ${STATUS_LABEL[s.status]}`}
          data-testid={`summary-banner-${s.summaryId}`}
          action={
            <button type="button" className={actionClass} onClick={() => setOpenId(s.summaryId)}>
              <FileText className="h-4 w-4" aria-hidden="true" /> Open summary
            </button>
          }
        >
          {s.dueState === 'SENT'
            ? `Sent ${formatUsDate(s.deliveries[s.deliveries.length - 1]?.sentDate)}.`
            : `${DUE_LABEL[s.dueState]} ${formatUsDate(s.dueDate)} — send it to the ${s.kind === 'TRANSFER' ? 'receiving facility' : 'primary care practitioner'} and mark it sent.`}
        </Banner>
      ))}
      {missingDischarge && canUpdate && (
        <Banner
          variant="info"
          title="No discharge summary yet"
          action={
            <button type="button" className={actionClass} disabled={creating} onClick={create}>
              <FileText className="h-4 w-4" aria-hidden="true" /> {creating ? 'Creating…' : 'Create discharge summary'}
            </button>
          }
        >
          Create it from the record, then complete and sign it.
        </Banner>
      )}
      <DischargeSummaryDialog
        open={Boolean(openId)}
        patientId={patientId}
        summaryId={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => void load()}
      />
    </>
  );
};

export default DischargeSummaryBanner;
